import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
//helpers
import { getTypeProduct } from 'helpers';
//api
import { deleteProduct, getProductsCountsTotalStock } from 'api/products';
import { getProducts } from 'api/pathForHook/products';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { formatCurrency } from 'utils/currencyFormatter';
//components
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import Title from 'components/backoffice/texts/title';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import FiltersTableProducts from './FiltersTableProducts';

const Products = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [listProducts, setListProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [idItem, setIdItem] = useState();
  const [formFilter, setFormFilter] = useState({});
  const [responseInfo, setResponseInfo] = useState({});
  const [totalProducts, setTotalProducts] = useState(null);
  const [totalProductsWithStock, setTotalProductsWithStock] = useState(null);
  const { setTitle } = useOutletContext()

  const { t } = useTranslation(['backoffice']);

  const colsProducts = ['Id', t('ean'), t('titulo'), t('product.producttype'), t('precioventapublico'), t('actions')];

  const { fetchData, stateData, loading } = useHandleSearch();

  const getListProducts = async () => {
    try {
      let payload = {
        page,
        page_size: 30,
        ean: formFilter.ean || '',
        titulo: formFilter.titulo || '',
        type: formFilter.type || '',
        fields: 'id,ean,title,type,price',
      }
      await fetchData(getProducts(), payload);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    setTitle(t('productos'));
    return () => {
      setTitle('');
    };
  }, [setTitle]);

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setListProducts(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setListProducts(null);
      setResponseInfo({ errors })
    }
  }, [stateData])


  useEffect(() => {
    getListProducts();
  }, [page, formFilter]);

  useEffect(() => {
    const fetchTotals = async () => {
      try {
        const response = await getProductsCountsTotalStock();
        
        if (response?.status === 200) {
          setTotalProducts(response?.data?.total_products || null);
          setTotalProductsWithStock(response?.data?.total_products_with_stock || null);
        }
      } catch (error) {
        console.error('Error fetching totals:', error);
      }
    };
    
    //fetchTotals();
  }, []);

  const handleClear = () => {
    setFormFilter({
      ean: '',
      titulo: '',
      type: 'Ninguno',
    });
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
  };

  const handleCreate = () => {
    navigate('/intranet/products/create');
  };

  const handleEdit = (id, type) => {
    navigate(`/intranet/products/edit/${id}?type=${type}`);
  };

  const handleDelete = (item, id, setOpenModal) => {
    if (item.id === id) {
      setOpenModal(true);
      setIdItem(id);
    }
  };

  const productDelete = useCallback(async () => {
    try {
      const { status } = await deleteProduct(idItem);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        setOpenModalDelete(false);
        getListProducts();
      } else {
        toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
      }
    } catch (error) {
      console.log(error);
    }
  }, [openModalDelete]);

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4">
        <Title text={t('productos')} />
        {totalProducts !== null && (
          <span className="product-stats-label mt-3 mt-md-5 mb-3">
            <span className="text-light">{t('productosTotales')}:</span> <span className="product-stats-value">{totalProducts}</span>
          </span>
        )}
        {totalProductsWithStock !== null && (
          <span className="product-stats-label mt-3 mt-md-5 mb-3">
            <span className="text-light">{t('stockTotal')}:</span> <span className="product-stats-value">{totalProductsWithStock}</span>
          </span>
        )}
      </div>
      <Row className="d-flex w-md-100 mt-3">
        <Col md={11} xs={12} className="d-flex text-start">
          <FiltersTableProducts
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            handleClear={handleClear}
            t={t}
            page={page}
            setPage={setPage}
          />
        </Col>
        <Col md={1} xs={12} className="d-flex text-end align-bottom p-md-0 justify-content-end pt-5 pt-md-0">
          {userPermissions.includes('producto.__add') && (
            <Button color="primary" onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsProducts}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {listProducts &&
            listProducts.map((listProduct, index) => {
              return (
                <tr key={listProduct.id}>
                  <td>{listProduct?.id}</td>
                  <td>{listProduct.ean}</td>
                  <td>{listProduct?.titulo}</td>
                  <td>{getTypeProduct(t, listProduct?.type)}</td>
                  <td>{formatCurrency(listProduct.price)}</td>
                  <td className=" item-dropdown text-end">
                    <DropdownIcons
                      idItem={index}
                      handleEdit={
                        userPermissions.includes('producto.__modify') &&
                        (() => handleEdit(listProduct.id, listProduct.type))
                      } 
                      handleDelete={userPermissions.includes('producto.__delete') && (() => handleDelete(listProduct, listProduct.id, setOpenModalDelete))}
                    />
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={productDelete}
        deleteTitle={t('deleteproduct')}
        beforeTitle={t('productos')}
      />
    </div>
  );
};

export default Products;